// app/src/lib/alerts/index.js — emitters for cross-cutting alerts from §12.
//
// Several alert *types* in the catalog aren't owned by any single feature module:
//   - par_below          (count batched; needs polling)
//   - bank_stale         (>24h since last Plaid sync)
//   - backup_stale       (>48h since last backup)
//   - new_vendor         (auto-emitted from invoiceSaver when a new vendor row is created)
//   - ocr_failed         (surfaced by client when OCR poll times out)
//
// This module owns those emitters with consistent dedup keys + severities so the
// catalog stays in sync with what actually surfaces on Today.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS  = 24 * ONE_HOUR_MS;

async function upsert(userId, kind, severity, title, body, meta) {
  // Dedupe by (userId, kind, meta.dedupeKey).
  const existing = await db.alerts.where('userId').equals(userId)
    .filter((a) => a.kind === kind && a.meta?.dedupeKey === meta.dedupeKey)
    .first();
  const row = {
    alertId: existing?.alertId || crypto.randomUUID(),
    userId, severity, kind, title, body,
    createdAt: existing?.createdAt || Date.now(),
    dismissed: existing?.dismissed ?? 0,
    meta,
  };
  await db.alerts.put(row);
  return row;
}

// ── par_below ─────────────────────────────────────────────────────────────

/**
 * Batched alert: emit once with a count when any items are below par.
 * Dedupe key = ISO date so it refreshes daily, not per-item.
 */
export async function checkParBelow() {
  const userId = await requireUserId();
  const pars = await db.parItems.where('userId').equals(userId).toArray();
  let below = 0;
  for (const p of pars) {
    const onHand = Number(p.onHandQuantity ?? 0);
    if (onHand < p.reorderPoint) below += 1;
  }
  if (below === 0) {
    // If a previous alert exists for today, dismiss it so the UI clears.
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const ex = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'PAR_BELOW' && a.meta?.dedupeKey === today)
      .first();
    if (ex) { ex.dismissed = 1; await db.alerts.put(ex); }
    return null;
  }
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  return upsert(userId, 'PAR_BELOW', 'info',
    `${below} item${below === 1 ? '' : 's'} below par`,
    'Open the Order Guide to review reorder quantities.',
    { dedupeKey: today, belowParCount: below });
}

// ── bank_stale ────────────────────────────────────────────────────────────

export async function checkBankStale() {
  const userId = await requireUserId();
  const items = await db.plaidItems.where('userId').equals(userId).toArray();
  if (!items.length) return null;
  const newest = Math.max(...items.map((i) => i.lastSyncedAt || 0));
  if (newest === 0 || (Date.now() - newest) <= ONE_DAY_MS) {
    // Dismiss any stale alert if we're back in sync.
    const ex = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'BANK_STALE')
      .first();
    if (ex && !ex.dismissed) { ex.dismissed = 1; await db.alerts.put(ex); }
    return null;
  }
  return upsert(userId, 'BANK_STALE', 'info',
    'Bank sync is out of date',
    'Open the app on a connected device, or reconnect to refresh.',
    { dedupeKey: 'global', lastSyncedAt: newest });
}

// ── backup_stale ──────────────────────────────────────────────────────────

const BACKUP_STALENESS_MS = 48 * ONE_HOUR_MS;

export async function checkBackupStale() {
  const userId = await requireUserId();
  // lastBackupAt is the only authoritative source (localStorage allow-listed).
  const last = Number(localStorage.getItem('lastBackupAt') || 0);
  if (last && (Date.now() - last) <= BACKUP_STALENESS_MS) {
    const ex = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'BACKUP_STALE')
      .first();
    if (ex && !ex.dismissed) { ex.dismissed = 1; await db.alerts.put(ex); }
    return null;
  }
  return upsert(userId, 'BACKUP_STALE', 'info',
    'Backup is overdue',
    'Open Settings → Backup now, or leave the app open so an automatic backup can run.',
    { dedupeKey: 'global', lastBackupAt: last });
}

// ── new_vendor ────────────────────────────────────────────────────────────

/** Emit when a brand-new vendor row was just created from OCR. Caller passes vendorId. */
export async function emitNewVendor(vendorId) {
  const userId = await requireUserId();
  const vendor = await db.vendors.get(vendorId);
  if (!vendor || vendor.userId !== userId) return null;
  return upsert(userId, 'NEW_VENDOR', 'info',
    `New vendor: ${vendor.name}`,
    'Open the vendor to set its payment terms.',
    { dedupeKey: vendorId, vendorId });
}

/** Clear a vendor's NEW_VENDOR alert once it's been handled (e.g. terms set). */
export async function dismissNewVendor(vendorId) {
  const userId = await requireUserId();
  const ex = await db.alerts.where('userId').equals(userId)
    .filter((a) => a.kind === 'NEW_VENDOR' && a.meta?.vendorId === vendorId)
    .first();
  if (ex && !ex.dismissed) { ex.dismissed = 1; await db.alerts.put(ex); }
}

/**
 * Self-healing: dismiss any open NEW_VENDOR alert whose vendor already has
 * payment terms set. Covers alerts created before the per-save dismissal existed
 * (or terms set on another device). Safe to call on every dashboard load.
 * @returns {Promise<number>} how many were dismissed
 */
export async function reconcileNewVendorAlerts() {
  const userId = await requireUserId();
  const open = await db.alerts.where('userId').equals(userId)
    .filter((a) => a.kind === 'NEW_VENDOR' && !a.dismissed).toArray();
  if (!open.length) return 0;
  const vids = Array.from(new Set(open.map((a) => a.meta?.vendorId).filter(Boolean)));
  const vendors = vids.length ? await db.vendors.where('vendorId').anyOf(vids).toArray() : [];
  const withTerms = new Set(vendors.filter((v) => v.paymentMethod).map((v) => v.vendorId));
  const existing = new Set(vendors.map((v) => v.vendorId));
  let n = 0;
  for (const a of open) {
    const vid = a.meta?.vendorId;
    // Dismiss once the vendor has payment terms set, OR if it no longer exists
    // (deleted / merged into another vendor — the "set its terms" nudge is moot).
    if (vid && (withTerms.has(vid) || !existing.has(vid))) { a.dismissed = 1; await db.alerts.put(a); n++; }
  }
  if (n > 0) { try { markBooksDirty(); } catch { /* non-fatal */ } }
  return n;
}

// ── ocr_failed ────────────────────────────────────────────────────────────

/** Emit when the OCR poll times out or the result endpoint returns a terminal error. */
export async function emitOcrFailed(receiptId, reason) {
  const userId = await requireUserId();
  return upsert(userId, 'OCR_FAILED', 'warning',
    'A scanned receipt failed to process',
    reason || 'Try re-scanning, or upload the invoice manually.',
    { dedupeKey: receiptId, receiptId });
}

// ── Composite tick: run every cross-cutting check. Call on app focus / route change. ─

export async function tickCrossCutting() {
  await Promise.all([
    checkParBelow().catch(() => null),
    checkBankStale().catch(() => null),
    checkBackupStale().catch(() => null),
  ]);
}
